import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useState } from 'react';
import { useAtomValue, useStore } from 'jotai';
import { Subscription } from 'rxjs';
import { SocketStatus } from '../../../data-models/socket-base.data-model';
import {
  ChatSocketMessageType,
  IChatMessage,
  ISocketConnectedResponse,
  SocketAgentActivity,
  SocketAiResponseStart,
  SocketConversationTitleUpdated,
  SocketErrorResponse,
  SocketGenerationCancelled,
  SocketMessageResponseChunk,
  SocketMessageResponseComplete,
  SocketNavigate,
  SocketViewSaved,
} from '../../../schemas/agent-conversation.schema';
import { HudsonWsService } from '../../../services/HudsonWsService';
import { readCustomerTenantId } from '../../../services/state/AuthState';
import { useToken } from '../../../services/hooks/useToken';
import { assistantSocketHeldAtom } from '../../../components/ChatBotSection/assistantAttentionStateJ';
import { useResetChatState } from './chatActionHooks';
import { useReconnectOnTenantChange } from './useReconnectOnTenantChange';

interface IUseWsChatParams {
  onFullMessageResponse?: (update: SocketMessageResponseComplete) => void;
  onConnectionEstablished?: (update: ISocketConnectedResponse) => void;
  onAiResponseStart?: (update: SocketAiResponseStart) => void;
  onMessageResponseChunk?: (update: SocketMessageResponseChunk) => void;
  onTitleUpdated?: (update: SocketConversationTitleUpdated) => void;
  onError?: (update: SocketErrorResponse) => void;
  onGenerationCancelled?: (update: SocketGenerationCancelled) => void;
  onAgentActivity?: (update: SocketAgentActivity) => void;
  onViewSaved?: (update: SocketViewSaved) => void;
  onNavigate?: (update: SocketNavigate) => void;
}
export function useWsChat({
  onError,
  onConnectionEstablished,
  onAiResponseStart,
  onFullMessageResponse,
  onGenerationCancelled,
  onMessageResponseChunk,
  onTitleUpdated,
  onAgentActivity,
  onViewSaved,
  onNavigate,
}: IUseWsChatParams) {
  const getToken = useToken();
  const resetChatState = useResetChatState();
  const store = useStore();
  // While the app holds the socket open for the session (AssistantSocket), the panel only
  // subscribes: it neither reconnects nor disconnects it, and the identity outlives the panel.
  const held = useAtomValue(assistantSocketHeldAtom);
  useReconnectOnTenantChange(!held);

  const sendWsMessage = useCallback((message: IChatMessage) => {
    if (!HudsonWsService.get()) {
      // should not happen
      Sentry.logger.error('Service not initialized');
      return;
    }
    HudsonWsService.get().send(message);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const subs: Subscription[] = [];
    Sentry.logger.info('subscribing to chat');

    HudsonWsService.initService(getToken, readCustomerTenantId).then(() => {
      if (cancelled) {
        return;
      } // Don't subscribe if unmounted

      const connectionSub = HudsonWsService.get().listen<ISocketConnectedResponse>(
        ChatSocketMessageType.connectionEstablished,
        (update) => {
          Sentry.logger.info('ws chat connection established');
          onConnectionEstablished?.(update);
        }
      );
      const messageSub = HudsonWsService.get().listen<SocketMessageResponseComplete>(
        ChatSocketMessageType.aiResponseComplete,
        (update) => {
          onFullMessageResponse?.(update);
        }
      );

      const messageChunkSub = HudsonWsService.get().listen<SocketMessageResponseChunk>(
        ChatSocketMessageType.aiResponseChunk,
        (update) => {
          onMessageResponseChunk?.(update);
        }
      );

      const aiResponseStartSub = HudsonWsService.get().listen<SocketAiResponseStart>(
        ChatSocketMessageType.aiResponseStart,
        (update) => {
          onAiResponseStart?.(update);
        }
      );

      const titleUpdatedSub = HudsonWsService.get().listen<SocketConversationTitleUpdated>(
        ChatSocketMessageType.titleUpdated,
        (update) => {
          onTitleUpdated?.(update);
        }
      );

      const onGenerationCancelledSub = HudsonWsService.get().listen<SocketGenerationCancelled>(
        ChatSocketMessageType.generationCanceled,
        (update) => {
          onGenerationCancelled?.(update);
        }
      );

      const onErrorSub = HudsonWsService.get().listen<SocketErrorResponse>(
        ChatSocketMessageType.error,
        (update) => {
          onError?.(update);
        }
      );

      const agentActivitySub = HudsonWsService.get().listen<SocketAgentActivity>(
        ChatSocketMessageType.agentActivity,
        (update) => {
          onAgentActivity?.(update);
        }
      );

      const viewSavedSub = HudsonWsService.get().listen<SocketViewSaved>(
        ChatSocketMessageType.viewSaved,
        (update) => {
          onViewSaved?.(update);
        }
      );

      const navigateSub = HudsonWsService.get().listen<SocketNavigate>(
        ChatSocketMessageType.navigate,
        (update) => {
          onNavigate?.(update);
        }
      );

      subs.push(
        connectionSub,
        messageSub,
        messageChunkSub,
        aiResponseStartSub,
        titleUpdatedSub,
        onGenerationCancelledSub,
        onErrorSub,
        agentActivitySub,
        viewSavedSub,
        navigateSub
      );
    });

    return () => {
      Sentry.logger.info('unsubscribing from chat');
      cancelled = true;
      subs.forEach((sub) => sub.unsubscribe());
    };
  }, [
    getToken,
    onConnectionEstablished,
    onAiResponseStart,
    onFullMessageResponse,
    onMessageResponseChunk,
    onTitleUpdated,
    onGenerationCancelled,
    onError,
    onAgentActivity,
    onViewSaved,
    onNavigate,
    resetChatState,
  ]);

  useEffect(() => {
    return () => {
      const heldOpen = store.get(assistantSocketHeldAtom);
      if (!heldOpen) {
        HudsonWsService.get()?.disconnect();
      }
      resetChatState({ keepIdentity: heldOpen });
    };
  }, [resetChatState, store]);

  return {
    sendMessage: sendWsMessage,
  };
}

export function useChatWsStatus(): SocketStatus | undefined {
  const [status, setStatus] = useState<SocketStatus>();

  useEffect(() => {
    const wsService = HudsonWsService.get();
    if (!wsService) {
      return;
    }

    const sub = wsService.socketStatus.subscribe(setStatus);
    return () => sub.unsubscribe();
  }, []);

  return status;
}
